import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { SPLASH_HOLD_MAX_MS } from './splashHoldConfig';

/**
 * Splash signal store.
 *
 * The CrewVex splash is a SINGLE DOM element, mounted once at the app root
 * and never unmounted for the lifetime of the session. Components that used
 * to render their own `<CrewVexLoadingScreen />` now call `useSplash(active)`
 * to *signal* that something is loading; the singleton overlay mirrors those
 * signals.
 *
 * Why: each previous splash callsite (Suspense fallback, ProtectedRoute
 * recovery, PlatformRoute recovery, BootGate, Login verification) created a
 * fresh React component and therefore a fresh DOM element. CSS animations
 * restart at position 0 on every new element — so the shimmer visibly
 * "reloaded" on every loading-state change. That's the flashing the user
 * saw. A singleton whose DOM never goes away means the shimmer is a single
 * continuous animation across all of these transitions.
 *
 * Signals are ref-counted: many callers can be active at once (e.g. Boot +
 * Suspense overlap during startup); the overlay stays visible until the
 * last one clears.
 *
 * Each signal can opt in to `immediate: true`, which bypasses the default
 * 180ms flash-protection delay on the overlay. Use it for intentional,
 * long-lived splashes (boot, login verification) where we want the splash
 * to appear on the very first commit and hand off seamlessly from the HTML
 * preloader.
 */

const SplashContext = createContext(null);

export function SplashProvider({ children }) {
  // Map of caller id → { immediate }. Object so useMemo + identity works.
  const [signals, setSignals] = useState({});

  const register = useCallback((id, opts) => {
    setSignals((prev) => {
      // If already registered with the same shape, bail to keep identity stable.
      const existing = prev[id];
      if (existing && existing.immediate === !!opts?.immediate && existing.hold === !!opts?.hold && existing.veil === !!opts?.veil) return prev;
      return { ...prev, [id]: { immediate: !!opts?.immediate, hold: !!opts?.hold, veil: !!opts?.veil } };
    });
  }, []);

  const unregister = useCallback((id) => {
    setSignals((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  // HOLD signals (KAN-562, `useSplashHold`) never raise the splash on their
  // own: they only keep it up after every other signal has cleared, and for at
  // most SPLASH_HOLD_MAX_MS of that — then the splash goes exactly as it would
  // have without them. Spent stays spent until every hold is released, so a
  // later blip can never re-arm the linger: it can never hang.
  const ids = Object.keys(signals);
  const primaryActive = ids.some((id) => !signals[id].hold);
  const holdActive = ids.some((id) => signals[id].hold);
  const [lingerSpent, setLingerSpent] = useState(false);
  useEffect(() => {
    if (!holdActive) { setLingerSpent(false); return undefined; }
    if (primaryActive || lingerSpent) return undefined;
    const t = setTimeout(() => setLingerSpent(true), SPLASH_HOLD_MAX_MS);
    return () => clearTimeout(t);
  }, [holdActive, primaryActive, lingerSpent]);

  const value = useMemo(() => {
    const lingering = holdActive && !lingerSpent;
    const active = primaryActive || lingering;
    const hasImmediate = ids.some((id) => signals[id].immediate && !signals[id].hold) || (!primaryActive && lingering);
    // VEIL (KAN-916): the overlay paints the sign-in ground, not the brand look,
    // only when EVERY raising signal asked for it — a veil never upgrades another
    // signal, and a hold-only linger is always the brand splash.
    const veil = primaryActive && ids.every((id) => signals[id].hold || signals[id].veil);
    return { signals, active, hasImmediate, veil, register, unregister };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signals, register, unregister, primaryActive, holdActive, lingerSpent]);

  return <SplashContext.Provider value={value}>{children}</SplashContext.Provider>;
}

function useSplashStore() {
  const ctx = useContext(SplashContext);
  if (!ctx) {
    throw new Error('useSplash must be used inside <SplashProvider>.');
  }
  return ctx;
}

/**
 * Subscribe the singleton splash to a local loading condition.
 *
 * Call it unconditionally (React rules of hooks), and pass `active` to
 * describe whether this component currently wants the splash up. Pass
 * `immediate: true` to bypass the overlay's default 180ms flash-protection
 * delay — for surfaces that are intentionally long-lived (boot, login).
 * Pass `veil: true` for the quiet sign-out cover: while every raising signal is
 * a veil, the overlay shows the sign-in ground instead of the brand splash.
 *
 * The splash stays up as long as *any* call to `useSplash` is active.
 *
 * ```jsx
 * // Inside a component:
 * const blocking = loading || recovering;
 * useSplash(blocking);
 * if (blocking) return null; // don't render guarded content yet
 * ```
 */
export function useSplash(active, { immediate = false, optional = false, hold = false, veil = false } = {}) {
  const id = useId();
  // `optional` callers (enhancement hooks like useFirstLoadSplash) no-op when
  // there's no provider — e.g. a presentational skeleton rendered in isolation
  // in a unit test. Default stays strict to catch genuine misuse.
  const ctx = useContext(SplashContext);
  if (!ctx && !optional) {
    throw new Error('useSplash must be used inside <SplashProvider>.');
  }
  const register = ctx?.register;
  const unregister = ctx?.unregister;

  useEffect(() => {
    if (!active || !register) return undefined;
    register(id, { immediate, hold, veil });
    return () => unregister(id);
  }, [id, active, immediate, hold, veil, register, unregister]);
}

/**
 * Suspense-fallback-friendly signaler. Renders nothing but pushes a deferred
 * splash signal for as long as it's mounted. Use as:
 *
 *   <Suspense fallback={<SplashTrigger />}> ... </Suspense>
 */
export function SplashTrigger({ immediate = false } = {}) {
  useSplash(true, { immediate });
  return null;
}

/**
 * Internal: read the aggregated splash state. Only the SplashOverlay should
 * need this.
 */
export function useSplashState() {
  const { active, hasImmediate, veil } = useSplashStore();
  return { active, hasImmediate, veil };
}
